import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowRight, Download, Star } from 'lucide-react';
import { BeforeAfter } from '../BeforeAfter';

const fa = (value: number) => value.toLocaleString('fa-IR');
const date = (value: string) => new Date(value).toLocaleString('fa-IR', { dateStyle: 'short', timeStyle: 'short' });

function statusLabel(value: string) {
  return ({ COMPLETED: 'تکمیل‌شده', FAILED: 'ناموفق', PROCESSING: 'در حال پردازش', PENDING: 'در انتظار' } as Record<string, string>)[value] || value;
}

async function downloadImage(url: string) {
  const downloadUrl = url.includes('?') ? `${url}&format=jpeg` : `${url}?format=jpeg`;
  try {
    const response = await fetch(downloadUrl);
    if (!response.ok) throw new Error('failed');
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = 'karolight-design.jpg';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(objectUrl);
  } catch {
    window.open(downloadUrl, '_blank', 'noopener');
  }
}

export function AdminGallery({ generations }: { generations: any[] }) {
  const [filter, setFilter] = useState<'ALL' | 'COMPLETED' | 'FAILED'>('ALL');
  const [active, setActive] = useState<any | null>(null);

  const items = useMemo(() => generations.filter((generation) => filter === 'ALL' || generation.status === filter), [generations, filter]);

  useEffect(() => {
    if (!active) return;
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') setActive(null); };
    window.addEventListener('keydown', onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = previousOverflow; };
  }, [active]);

  return <div className="admin-gallery">
    <div className="gallery-filters">
      {([['ALL', 'همه'], ['COMPLETED', 'تکمیل‌شده'], ['FAILED', 'ناموفق']] as const).map(([value, label]) => (
        <button key={value} className={filter === value ? 'active' : ''} onClick={() => setFilter(value)}>{label}</button>
      ))}
      <span className="gallery-count">{fa(items.length)} خروجی</span>
    </div>
    {items.length ? <div className="gallery-grid">
      {items.map((generation) => (
        <button key={generation.id} className="gallery-card" onClick={() => setActive(generation)}>
          {generation.afterImageUrl
            ? <img src={generation.afterImageUrl} alt={`خروجی طراحی ${generation.session?.user?.name || ''}`} loading="lazy" />
            : <span className="gallery-placeholder">بدون تصویر</span>}
          <span className={`gallery-status ${String(generation.status).toLowerCase()}`}>{statusLabel(generation.status)}</span>
          <span className="gallery-caption">
            <b>{generation.session?.user?.name || 'کاربر مهمان'}</b>
            <small>{date(generation.createdAt)}</small>
          </span>
        </button>
      ))}
    </div> : <div className="empty-admin">خروجی‌ای برای این فیلتر وجود ندارد.</div>}

    {active && createPortal(<div className="comparison-lightbox admin-lightbox" role="dialog" aria-modal="true" onClick={(event) => { if (event.target === event.currentTarget) setActive(null); }}>
      <div className="lightbox-bar">
        <strong>{active.session?.user?.name || 'کاربر مهمان'} · {date(active.createdAt)}</strong>
        <div className="lightbox-actions">
          {active.survey && <span className="lightbox-rating"><Star size={15} /> {fa(active.survey.rating)} از ۵</span>}
          {active.afterImageUrl && <button type="button" className="primary-button" onClick={() => void downloadImage(active.afterImageUrl)}><Download size={16} />دانلود تصویر</button>}
          <button type="button" className="icon-button lightbox-back" onClick={() => setActive(null)} aria-label="بازگشت به گالری"><ArrowRight size={17} />بازگشت به گالری</button>
        </div>
      </div>
      {active.beforeImageUrl && active.afterImageUrl
        ? <div className="lightbox-comparison"><BeforeAfter before={active.beforeImageUrl} after={active.afterImageUrl} /></div>
        : <div className="lightbox-comparison gallery-single"><img src={active.afterImageUrl || active.beforeImageUrl} alt="تصویر طراحی" /></div>}
      {active.products?.length > 0 && <div className="lightbox-products">{active.products.map((product: any) => <a key={product.id} href={product.storeUrl} target="_blank" rel="noreferrer">{product.name}</a>)}</div>}
      <small className="lightbox-hint">خط مقایسه را بکشید؛ Esc می‌بندد.</small>
    </div>, document.body)}
  </div>;
}
